// app/frontend/components/ui/MenuNavItem.tsx
import { Anchor, Group, Text, Box } from "@mantine/core";
import { Link } from "@inertiajs/react";
import { Icon } from "@tabler/icons-react";
import React from "react";

export interface MenuNavItemProps {
  /**
   * URL path for the navigation link
   */
  href: string;
  /**
   * Icon component from @tabler/icons-react
   */
  icon: Icon;
  /**
   * Label text to display
   */
  label: string;
  /**
   * Optional click handler (e.g., to close menu)
   */
  onClick?: () => void;
  /**
   * Optional custom styles
   */
  style?: React.CSSProperties;
  /**
   * Whether the item is disabled (non-clickable)
   */
  disabled?: boolean;
}

/**
 * MenuNavItem - Reusable navigation item component for menu drawers
 *
 * Features:
 * - Icon + label layout
 * - Smooth hover color transitions
 * - Consistent styling and spacing
 * - Click handler support
 *
 * @example
 * <MenuNavItem
 *   href="/videos"
 *   icon={IconVideo}
 *   label="Free Videos"
 *   onClick={() => closeMenu()}
 * />
 */
export function MenuNavItem({
  href,
  icon: IconComponent,
  label,
  onClick,
  style,
  disabled = false,
}: MenuNavItemProps) {
  const [iconColor, setIconColor] = React.useState(
    disabled ? "rgba(255, 255, 255, 0.3)" : "#F0F0F0"
  );
  const [textColor, setTextColor] = React.useState(
    disabled ? "rgba(255, 255, 255, 0.3)" : "#F0F0F0"
  );

  const handleMouseEnter = () => {
    if (!disabled) {
      setIconColor("#FF9800");
      setTextColor("#FF9800");
    }
  };

  const handleMouseLeave = () => {
    if (!disabled) {
      setIconColor("#F0F0F0");
      setTextColor("#F0F0F0");
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (onClick) {
      onClick();
    }
  };

  const content = (
    <Group gap="sm">
      <IconComponent
        size={20}
        style={{ color: iconColor, transition: "color 0.2s ease" }}
      />
      <Text
        size="sm"
        fw={500}
        style={{ color: textColor, transition: "color 0.2s ease" }}
      >
        {label}
      </Text>
    </Group>
  );

  const commonStyle = {
    color: textColor,
    textDecoration: "none" as const,
    padding: "8px 0",
    cursor: disabled ? ("not-allowed" as const) : ("pointer" as const),
    transition: "color 0.2s ease",
    opacity: disabled ? 0.5 : 1,
    ...style,
  };

  if (disabled) {
    return (
      <Box
        style={commonStyle}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {content}
      </Box>
    );
  }

  return (
    <Anchor
      component={Link}
      href={href}
      style={commonStyle}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {content}
    </Anchor>
  );
}
